import {HttpClient, HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {ApiServiceUtils} from 'projects/ngx-asr-lib/src/lib/services/api-service-utils';
import * as settings from 'projects/ngx-asr-lib/src/lib/settings/settings.json';
import {from, of, Subject, Subscription, timer} from 'rxjs';
import {catchError, finalize, map, mergeMap, takeUntil, tap} from 'rxjs/operators';

export type ApiMethod = 'GET' | 'POST';

export interface ApiEndpointDef {
  id: string;
  name: string;
  method: ApiMethod;
  group: string;
  /** Path after `{api}/` or absolute auth path starting with `auth/` */
  pathTemplate: string;
  description: string;
  /** Needs a reviewId substitution */
  needsReviewId?: boolean;
  /** Needs a reviewerId substitution */
  needsReviewerId?: boolean;
  /** Heavy / binary — opt-in only */
  heavy?: boolean;
  /** Known mutating GET — never auto-run */
  mutating?: boolean;
  /** Auth service host instead of ASR API */
  authHost?: boolean;
  selected?: boolean;
  /**
   * POST safety class:
   * - mutating: creates/updates data — dry-run only (never sent)
   * - download: PDF/export POST — still default dry-run; optional network later
   */
  postSafety?: 'mutating' | 'download';
  /** Representative body used only for client-side dry-run serialization */
  sampleBody?: any;
}

export interface ApiRunResult {
  id: string;
  name: string;
  method: ApiMethod;
  url: string;
  status: number | null;
  ok: boolean;
  durationMs: number;
  bytes: number;
  error?: string;
  iteration: number;
  startedAt: string;
  /** e.g. DRY when request was not sent */
  label?: string;
  preview?: string;
}

export type EndpointRunState = 'idle' | 'queued' | 'running' | 'ok' | 'fail';

export interface EndpointRowStatus {
  state: EndpointRunState;
  /** Live elapsed seconds while running */
  liveSec: number;
  durationMs: number | null;
  status: number | null;
  error?: string;
  doneCount: number;
  totalCount: number;
  startedAt: number | null;
  /** Display override such as DRY */
  label?: string;
  bytes?: number;
  /** Preview of last dry-run body */
  preview?: string;
}

export interface UiRouteDef {
  id: string;
  title: string;
  group: string;
  /** Path template with :reviewId / :userId / etc. */
  pathTemplate: string;
  description: string;
  needsReviewId?: boolean;
  needsUserId?: boolean;
  needsProgramParams?: boolean;
  needsItemId?: boolean;
  /** Expected API path fragments this page typically calls (coverage check) */
  expectedApis: string[];
  selected?: boolean;
}

export interface UiRouteRowStatus {
  state: EndpointRunState;
  liveSec: number;
  durationMs: number | null;
  loadMs: number | null;
  settleMs: number | null;
  ok: boolean | null;
  error?: string;
  discoveredApis: string[];
  missingExpected: string[];
  extraApis: string[];
  startedAt: number | null;
  httpErrors: number;
  /** Live phase label while the page is loading (not a blind spinner) */
  phase?: string;
}

export interface ActivityStep {
  id: string;
  label: string;
  state: 'pending' | 'active' | 'done' | 'fail' | 'skip';
  detail?: string;
  at?: string;
}

export interface ErrorPrompt {
  title: string;
  message: string;
  details?: string;
  source: 'ui' | 'api' | 'system';
  path?: string;
}

@Component({
  selector: 'app-asrapitest',
  templateUrl: './asrapitest.component.html',
  styleUrls: ['./asrapitest.component.scss']
})
export class AsrApiTestComponent implements OnInit, OnDestroy {
  form: FormGroup;
  apiBase = '';
  authBase = '';
  tenant = '';
  origin = '';

  /** api | ui */
  activeTab: 'api' | 'ui' = 'api';

  /** Entire harness is DSD-only (checked in-component; no other files changed). */
  accessChecked = false;
  isDsd = false;
  accessDeniedReason = '';
  currentUserRoles: string[] = [];

  getEndpoints: ApiEndpointDef[] = [];
  postEndpoints: ApiEndpointDef[] = [];
  uiRoutes: UiRouteDef[] = [];

  /** Per-endpoint live status shown in the GET table */
  rowStatus: {[id: string]: EndpointRowStatus} = {};
  uiRowStatus: {[id: string]: UiRouteRowStatus} = {};

  results: ApiRunResult[] = [];
  running = false;
  progressDone = 0;
  progressTotal = 0;
  summary = {
    total: 0,
    ok: 0,
    fail: 0,
    avgMs: 0,
    minMs: 0,
    maxMs: 0
  };
  uiSummary = {
    total: 0,
    ok: 0,
    fail: 0,
    avgMs: 0,
    minMs: 0,
    maxMs: 0,
    apisFound: 0
  };

  /** Shown under POST table after a dry-run completes */
  lastPostDryPreview: {
    name: string;
    url: string;
    bytes: number;
    durationMs: number;
    body: string;
  } | null = null;

  lastUiDiscover: {
    routeId: string;
    name: string;
    apis: string[];
  } | null = null;

  /** Live checklist of what the harness is doing (replaces blind spinner). */
  activitySteps: ActivityStep[] = [];
  activityTitle = '';
  activityCurrent = '';

  /** Modal prompt shown on any failure (UI or API). */
  errorPrompt: ErrorPrompt | null = null;
  /** Queue of failures when several occur; modal shows the first, rest remain listed. */
  errorQueue: ErrorPrompt[] = [];

  private stop$ = new Subject<void>();
  private subs = new Subscription();
  private tickTimer: any = null;
  private activeIframe: HTMLIFrameElement | null = null;
  private settleCountdownTimer: any = null;

  constructor(
    private http: HttpClient,
    private apiUtils: ApiServiceUtils,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    this.form = this.fb.group({
      reviewId: [''],
      reviewerId: [''],
      iterations: [1],
      concurrency: [3],
      includeHeavy: [false],
      postIterations: [1],
      postConcurrency: [3],
      uiSettleMs: [3000],
      uiTimeoutMs: [45000],
      autoUiOnLoad: [false],
      itemId: ['1'],
      userId: [''],
      programName: ['test-program'],
      programLeadId: ['1']
    });
  }

  ngOnInit(): void {
    this.apiBase = (this.apiUtils.getApiUrl() || '').replace(/\/$/, '');
    this.authBase = (this.apiUtils.authUrl || '').replace(/\/$/, '');
    this.tenant = this.apiUtils.tenant || '';
    this.origin = window.location.origin;
    this.getEndpoints = this.buildGetCatalog();
    this.postEndpoints = this.buildPostCatalog();
    this.uiRoutes = this.buildUiRouteCatalog();
    this.resetRowStatuses();
    this.resetUiRowStatuses();
    this.resolveDsdAccess();
  }

  ngOnDestroy(): void {
    this.destroyActiveIframe();
    this.stopTicker();
    this.clearSettleCountdown();
    this.stop$.next();
    this.stop$.complete();
    this.subs.unsubscribe();
  }

  setTab(tab: 'api' | 'ui'): void {
    if (!this.isDsd) {
      return;
    }
    this.activeTab = tab;
  }

  get uiSelectAllChecked(): boolean {
    return this.uiRoutes.length > 0 && this.uiRoutes.every(r => !!r.selected);
  }

  get uiSelectAllIndeterminate(): boolean {
    const n = this.uiRoutes.filter(r => r.selected).length;
    return n > 0 && n < this.uiRoutes.length;
  }

  onUiSelectAll(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectAllUi(!!(input && input.checked));
  }

  startUiTesting(): void {
    if (!this.isDsd || this.running) {
      return;
    }
    this.activeTab = 'ui';
    this.selectRunnableUi();
    this.executeUiLoads(this.selectedUiRoutes);
  }

  formatUiDetails(route: UiRouteDef): string {
    const s = this.uiStatusFor(route);
    const parts: string[] = [];
    if (s.error) {
      parts.push(s.error);
    }
    if (s.missingExpected && s.missingExpected.length) {
      parts.push('Missing expected: ' + s.missingExpected.join(', '));
    }
    if (s.state === 'ok' && (!s.discoveredApis || !s.discoveredApis.length) && route.expectedApis.length) {
      parts.push('Page OK but no ASR/auth API calls were captured in the Performance timeline.');
    }
    if (s.loadMs != null) {
      parts.push('iframe load ' + s.loadMs + 'ms');
    }
    return parts.length ? parts.join(' · ') : '—';
  }

  private resolveDsdAccess(): void {
    const dsdRole = (settings as any).codes.roles.dsd || 'DSD';
    const applyRoles = (roles: string[]) => {
      this.currentUserRoles = roles || [];
      this.isDsd = this.currentUserRoles.indexOf(dsdRole) >= 0;
      this.accessChecked = true;
      if (!this.isDsd) {
        this.accessDeniedReason =
          'This harness is restricted to users with the DSD role. Your roles: ' +
          (this.currentUserRoles.length ? this.currentUserRoles.join(', ') : '(none found)');
      } else {
        this.bootstrapUiContext();
      }
      this.cdr.detectChanges();
    };

    // Prefer session user (already loaded by shell), then refresh from API.
    try {
      const raw = window.sessionStorage.getItem(settings.codes.sessionStorage.currentUser);
      if (raw) {
        const cu = JSON.parse(raw);
        if (cu && Array.isArray(cu.asrRoles)) {
          applyRoles(cu.asrRoles);
        }
      }
    } catch (e) {
      /* ignore */
    }

    this.subs.add(
      this.http.get(`${this.apiBase}/users/currentUser`).subscribe({
        next: (data: any) => {
          const roles = (data && data.asrRoles) || [];
          try {
            window.sessionStorage.setItem(settings.codes.sessionStorage.currentUser, JSON.stringify(data));
          } catch (e) {
            /* ignore */
          }
          applyRoles(roles);
        },
        error: () => {
          if (!this.accessChecked) {
            this.accessChecked = true;
            this.isDsd = false;
            this.accessDeniedReason = 'Unable to load currentUser to verify DSD role. Sign in and reopen /asrapitest.';
            this.cdr.detectChanges();
          }
        }
      })
    );
  }

  get selectedGets(): ApiEndpointDef[] {
    return this.getEndpoints.filter(e => e.selected && !e.mutating);
  }

  get runnableGets(): ApiEndpointDef[] {
    const includeHeavy = !!this.form.get('includeHeavy').value;
    return this.selectedGets.filter(e => includeHeavy || !e.heavy);
  }

  statusFor(endpoint: ApiEndpointDef): EndpointRowStatus {
    return (
      this.rowStatus[endpoint.id] || {
        state: 'idle',
        liveSec: 0,
        durationMs: null,
        status: null,
        doneCount: 0,
        totalCount: 0,
        startedAt: null
      }
    );
  }

  formatElapsed(endpoint: ApiEndpointDef): string {
    const s = this.statusFor(endpoint);
    if (s.state === 'running' || s.state === 'queued') {
      if (s.liveSec < 1) {
        return Math.round(s.liveSec * 1000) + 'ms';
      }
      return s.liveSec.toFixed(1) + 's';
    }
    if (s.durationMs != null) {
      if (s.durationMs < 1000) {
        return s.durationMs + 'ms';
      }
      return (s.durationMs / 1000).toFixed(2) + 's';
    }
    return '—';
  }

  formatResult(endpoint: ApiEndpointDef): string {
    const s = this.statusFor(endpoint);
    if (s.label) {
      let text = s.label;
      if (s.bytes != null && s.bytes > 0) {
        text += ' · ' + s.bytes + ' B';
      }
      if (s.totalCount > 1) {
        text += ' (' + s.doneCount + '/' + s.totalCount + ')';
      }
      return text;
    }
    if (s.state === 'idle') {
      return '—';
    }
    if (s.state === 'queued') {
      return 'queued';
    }
    if (s.state === 'running') {
      return 'waiting…';
    }
    if (s.state === 'fail') {
      const failCode = s.status != null && s.status !== 0 ? String(s.status) : 'ERR';
      return failCode;
    }
    const code = s.status != null ? String(s.status) : 'err';
    if (s.totalCount > 1) {
      return code + ' (' + s.doneCount + '/' + s.totalCount + ')';
    }
    return code;
  }

  formatApiError(endpoint: ApiEndpointDef): string {
    const s = this.statusFor(endpoint);
    if (s.state !== 'fail' && s.label !== 'DRY-ERR') {
      return '';
    }
    return (s.error || 'Request failed').slice(0, 500);
  }

  get selectedPosts(): ApiEndpointDef[] {
    return this.postEndpoints.filter(e => e.selected);
  }

  selectAllPosts(checked: boolean): void {
    this.postEndpoints.forEach(e => {
      e.selected = checked;
    });
  }

  onPostSelect(endpoint: ApiEndpointDef, event: Event): void {
    const input = event.target as HTMLInputElement;
    endpoint.selected = !!(input && input.checked);
  }

  /**
   * Client-only POST dry-run: builds URL + sample JSON body and times serialization.
   * Never opens a network connection — safe for mutating endpoints.
   */
  runSelectedPostDryRuns(): void {
    this.executePostDryRuns(this.selectedPosts);
  }

  runAllPostDryRuns(): void {
    this.postEndpoints.forEach(e => (e.selected = true));
    this.executePostDryRuns(this.postEndpoints.slice());
  }

  get selectedUiRoutes(): UiRouteDef[] {
    return this.uiRoutes.filter(r => r.selected);
  }

  uiStatusFor(route: UiRouteDef): UiRouteRowStatus {
    return (
      this.uiRowStatus[route.id] || {
        state: 'idle',
        liveSec: 0,
        durationMs: null,
        loadMs: null,
        settleMs: null,
        ok: null,
        discoveredApis: [],
        missingExpected: [],
        extraApis: [],
        startedAt: null,
        httpErrors: 0
      }
    );
  }

  formatUiElapsed(route: UiRouteDef): string {
    const s = this.uiStatusFor(route);
    if (s.state === 'running' || s.state === 'queued') {
      if (s.liveSec < 1) {
        return Math.round(s.liveSec * 1000) + 'ms';
      }
      return s.liveSec.toFixed(1) + 's';
    }
    if (s.durationMs != null) {
      if (s.durationMs < 1000) {
        return s.durationMs + 'ms';
      }
      return (s.durationMs / 1000).toFixed(2) + 's';
    }
    return '—';
  }

  formatUiResult(route: UiRouteDef): string {
    const s = this.uiStatusFor(route);
    if (s.state === 'idle') {
      return '—';
    }
    if (s.state === 'queued') {
      return 'queued';
    }
    if (s.state === 'running') {
      return s.phase || 'working…';
    }
    if (s.ok) {
      return 'OK · ' + s.discoveredApis.length + ' APIs';
    }
    return 'FAIL';
  }

  dismissErrorPrompt(): void {
    if (this.errorQueue.length) {
      this.errorPrompt = this.errorQueue.shift() || null;
    } else {
      this.errorPrompt = null;
    }
    this.cdr.detectChanges();
  }

  dismissAllErrors(): void {
    this.errorPrompt = null;
    this.errorQueue = [];
    this.cdr.detectChanges();
  }

  get errorQueueCount(): number {
    return this.errorQueue.length + (this.errorPrompt ? 1 : 0);
  }

  private pushErrorPrompt(prompt: ErrorPrompt): void {
    if (!this.errorPrompt) {
      this.errorPrompt = prompt;
    } else {
      this.errorQueue = this.errorQueue.concat([prompt]);
    }
    this.cdr.detectChanges();
  }

  private resetActivity(title: string, steps: Array<{id: string; label: string}>): void {
    this.activityTitle = title;
    this.activityCurrent = '';
    this.activitySteps = steps.map(s => ({
      id: s.id,
      label: s.label,
      state: 'pending' as const
    }));
  }

  private setActivity(stepId: string, state: ActivityStep['state'], detail?: string): void {
    const now = new Date().toLocaleTimeString();
    this.activitySteps = this.activitySteps.map(s => {
      if (s.id !== stepId) {
        if (state === 'active' && s.state === 'active') {
          return Object.assign({}, s, {state: 'done' as const});
        }
        return s;
      }
      return Object.assign({}, s, {
        state,
        detail: detail != null ? detail : s.detail,
        at: now
      });
    });
    const active = this.activitySteps.find(s => s.state === 'active');
    const failed = this.activitySteps.find(s => s.state === 'fail');
    this.activityCurrent = failed
      ? failed.label + (failed.detail ? ': ' + failed.detail : '')
      : active
      ? active.label + (active.detail ? ' — ' + active.detail : '')
      : '';
    this.cdr.detectChanges();
  }

  private clearSettleCountdown(): void {
    if (this.settleCountdownTimer != null) {
      clearInterval(this.settleCountdownTimer);
      this.settleCountdownTimer = null;
    }
  }

  resolveUiPath(route: UiRouteDef): string {
    const reviewId = (this.form.get('reviewId').value || '0').toString().trim() || '0';
    const userId = (this.form.get('userId').value || '').toString().trim() || reviewId;
    const itemId = (this.form.get('itemId').value || '1').toString().trim() || '1';
    const programName = (this.form.get('programName').value || 'test-program').toString().trim();
    const programLeadId = (this.form.get('programLeadId').value || '1').toString().trim();
    return route.pathTemplate
      .replace(':reviewId', encodeURIComponent(reviewId))
      .replace(':userId', encodeURIComponent(userId))
      .replace(':itemId', encodeURIComponent(itemId))
      .replace(':programName', encodeURIComponent(programName))
      .replace(':programLeadId', encodeURIComponent(programLeadId));
  }

  selectAllUi(checked: boolean): void {
    this.uiRoutes.forEach(r => (r.selected = checked));
  }

  selectRunnableUi(): void {
    const reviewId = (this.form.get('reviewId').value || '').toString().trim();
    this.uiRoutes.forEach(r => {
      r.selected = !r.needsReviewId || !!reviewId;
      if (r.needsItemId && !(this.form.get('itemId').value || '').toString().trim()) {
        r.selected = false;
      }
    });
  }

  onUiSelect(route: UiRouteDef, event: Event): void {
    const input = event.target as HTMLInputElement;
    route.selected = !!(input && input.checked);
  }

  runSelectedUiLoads(): void {
    if (!this.isDsd) {
      return;
    }
    this.executeUiLoads(this.selectedUiRoutes);
  }

  runAllUiLoads(): void {
    if (!this.isDsd) {
      return;
    }
    this.selectRunnableUi();
    this.executeUiLoads(this.selectedUiRoutes);
  }

  private bootstrapUiContext(): void {
    // Resolve reviewId then optionally auto-run UI page-load suite.
    this.subs.add(
      this.http.get(`${this.apiBase}/reviews/my`).subscribe({
        next: (data: any) => {
          const first = Array.isArray(data) ? data[0] : null;
          const id = first && (first.id || first.Id || first.ID);
          if (id && !(this.form.get('reviewId').value || '').toString().trim()) {
            this.form.patchValue({reviewId: String(id)});
          }
          if (!(this.form.get('userId').value || '').toString().trim()) {
            try {
              const cu = JSON.parse(window.sessionStorage.getItem('currentUser') || 'null');
              if (cu && (cu.asrUserId || cu.id)) {
                this.form.patchValue({userId: String(cu.asrUserId || cu.id)});
              }
            } catch (e) {
              /* ignore */
            }
          }
          this.cdr.detectChanges();
          if (this.form.get('autoUiOnLoad').value) {
            // Defer so the UI tab catalog is painted first.
            setTimeout(() => {
              if (!this.running) {
                this.activeTab = 'ui';
                this.runAllUiLoads();
              }
            }, 400);
          }
        },
        error: () => {
          if (this.form.get('autoUiOnLoad').value) {
            setTimeout(() => {
              if (!this.running) {
                this.activeTab = 'ui';
                this.runAllUiLoads();
              }
            }, 400);
          }
        }
      })
    );
  }

  private resetUiRowStatuses(): void {
    const next: {[id: string]: UiRouteRowStatus} = {};
    this.uiRoutes.forEach(r => {
      next[r.id] = {
        state: 'idle',
        liveSec: 0,
        durationMs: null,
        loadMs: null,
        settleMs: null,
        ok: null,
        discoveredApis: [],
        missingExpected: [],
        extraApis: [],
        startedAt: null,
        httpErrors: 0,
        phase: undefined
      };
    });
    this.uiRowStatus = next;
  }

  private executeUiLoads(routes: UiRouteDef[]): void {
    if (this.running || !routes.length) {
      return;
    }
    this.stop$.next();
    this.stop$ = new Subject<void>();
    this.running = true;
    this.progressDone = 0;
    this.progressTotal = routes.length;
    this.uiSummary = {total: 0, ok: 0, fail: 0, avgMs: 0, minMs: 0, maxMs: 0, apisFound: 0};
    this.lastUiDiscover = null;
    this.resetActivity('UI page-load run', [
      {id: 'prepare', label: 'Prepare selected routes'},
      {id: 'open', label: 'Open route in iframe'},
      {id: 'load', label: 'Wait for document load'},
      {id: 'settle', label: 'Settle — let page APIs finish'},
      {id: 'harvest', label: 'Harvest Performance API entries'},
      {id: 'check', label: 'Compare expected vs discovered APIs'},
      {id: 'done', label: 'Route complete'}
    ]);
    this.setActivity('prepare', 'active', routes.length + ' route(s)');

    const nextStatus: {[id: string]: UiRouteRowStatus} = Object.assign({}, this.uiRowStatus);
    routes.forEach(r => {
      nextStatus[r.id] = {
        state: 'queued',
        liveSec: 0,
        durationMs: null,
        loadMs: null,
        settleMs: null,
        ok: null,
        discoveredApis: [],
        missingExpected: [],
        extraApis: [],
        startedAt: null,
        httpErrors: 0,
        phase: 'queued'
      };
    });
    this.uiRowStatus = nextStatus;
    this.startUiTicker();
    this.setActivity('prepare', 'done', routes.length + ' queued');
    this.cdr.detectChanges();

    // Sequential page loads — safer for auth + shared session.
    this.subs.add(
      from(routes)
        .pipe(
          mergeMap(route => this.runOneUiLoad(route), 1),
          takeUntil(this.stop$),
          finalize(() => {
            this.running = false;
            this.stopTicker();
            this.clearSettleCountdown();
            this.destroyActiveIframe();
            this.recomputeUiSummary();
            this.setActivity(
              'done',
              this.uiSummary.fail ? 'fail' : 'done',
              this.uiSummary.ok + ' ok / ' + this.uiSummary.fail + ' fail'
            );
            this.activityCurrent = this.uiSummary.fail
              ? 'UI run finished with ' + this.uiSummary.fail + ' failure(s) — see modal / Details'
              : 'UI run finished';
            this.cdr.detectChanges();
          })
        )
        .subscribe()
    );
  }

  private runOneUiLoad(route: UiRouteDef) {
    const t0 = performance.now();
    this.markUiRunning(route.id, t0, 'Opening…');
    this.resetActivity('UI: ' + route.title, [
      {id: 'prepare', label: 'Prepare selected routes'},
      {id: 'open', label: 'Open route in iframe'},
      {id: 'load', label: 'Wait for document load'},
      {id: 'settle', label: 'Settle — let page APIs finish'},
      {id: 'harvest', label: 'Harvest Performance API entries'},
      {id: 'check', label: 'Compare expected vs discovered APIs'},
      {id: 'done', label: 'Route complete'}
    ]);
    this.setActivity('prepare', 'done');
    this.setActivity('open', 'active', this.resolveUiPath(route));
    this.cdr.detectChanges();

    const path = this.resolveUiPath(route);
    const url = this.origin + '/' + path.replace(/^\//, '');
    const settleMs = Math.max(500, Math.min(20000, Number(this.form.get('uiSettleMs').value) || 3000));
    const timeoutMs = Math.max(5000, Math.min(120000, Number(this.form.get('uiTimeoutMs').value) || 45000));

    return new Promise<void>(resolve => {
      this.destroyActiveIframe();
      this.clearSettleCountdown();
      const iframe = document.createElement('iframe');
      iframe.setAttribute('title', 'asrapitest-ui-' + route.id);
      iframe.setAttribute('data-asrapitest', '1');
      iframe.style.cssText =
        'position:fixed;right:8px;bottom:8px;width:360px;height:220px;border:1px solid #c5d0dc;background:#fff;z-index:40;border-radius:2px;box-shadow:0 2px 10px rgba(0,0,0,.12)';
      this.activeIframe = iframe;

      let loadMs: number | null = null;
      let finished = false;
      const finish = (partial: Partial<UiRouteRowStatus>) => {
        if (finished) {
          return;
        }
        finished = true;
        window.clearTimeout(timerId);
        this.clearSettleCountdown();
        const durationMs = Math.round(performance.now() - t0);
        const discovered = partial.discoveredApis || [];
        const missing = (route.expectedApis || []).filter(exp => !discovered.some(d => d.indexOf(exp) >= 0));
        const extra = discovered.filter(d => !(route.expectedApis || []).some(exp => d.indexOf(exp) >= 0));
        const ok = partial.ok !== false && !partial.error;
        this.uiRowStatus = Object.assign({}, this.uiRowStatus, {
          [route.id]: Object.assign({}, this.uiStatusFor(route), partial, {
            state: (ok ? 'ok' : 'fail') as EndpointRunState,
            durationMs,
            loadMs,
            settleMs,
            ok,
            discoveredApis: discovered,
            missingExpected: missing,
            extraApis: extra,
            startedAt: null,
            liveSec: durationMs / 1000,
            phase: ok ? 'done' : 'failed'
          })
        });
        this.lastUiDiscover = {routeId: route.id, name: route.title, apis: discovered};
        this.progressDone += 1;
        this.recomputeUiSummary();

        if (ok) {
          this.setActivity('check', 'done', discovered.length + ' API(s)');
          this.setActivity('done', 'done', durationMs + 'ms');
        } else {
          const errMsg = partial.error || 'UI page check failed';
          this.setActivity('check', 'fail', errMsg);
          this.setActivity('done', 'fail', errMsg);
          this.pushErrorPrompt({
            title: 'UI route failed: ' + route.title,
            message: errMsg,
            details:
              'Path: ' +
              path +
              '\nLoad: ' +
              (loadMs != null ? loadMs + 'ms' : 'n/a') +
              '\nDiscovered: ' +
              (discovered.length ? discovered.join(', ') : '(none)') +
              (missing.length ? '\nMissing expected: ' + missing.join(', ') : ''),
            source: 'ui',
            path
          });
        }
        this.cdr.detectChanges();
        setTimeout(() => {
          if (this.activeIframe === iframe) {
            this.destroyActiveIframe();
          }
        }, 200);
        resolve();
      };

      const timerId = window.setTimeout(() => {
        this.setActivity('settle', 'fail', 'Timed out after ' + timeoutMs + 'ms');
        this.updateUiPhase(route.id, 'Timed out');
        const discovered = this.collectApisFromIframe(iframe);
        finish({
          ok: discovered.length > 0,
          error: discovered.length ? undefined : 'Timed out after ' + timeoutMs + 'ms waiting for page settle / APIs',
          discoveredApis: discovered,
          httpErrors: 0
        });
      }, timeoutMs);

      iframe.onload = () => {
        loadMs = Math.round(performance.now() - t0);
        this.setActivity('load', 'done', loadMs + 'ms');
        this.updateUiPhase(route.id, 'Settling…');
        this.setActivity('settle', 'active', settleMs + 'ms remaining');

        let remaining = settleMs;
        this.clearSettleCountdown();
        this.settleCountdownTimer = setInterval(() => {
          remaining = Math.max(0, remaining - 250);
          this.setActivity('settle', 'active', Math.ceil(remaining / 1000) + 's left · watching for APIs');
          this.updateUiPhase(route.id, 'Settling ' + Math.ceil(remaining / 1000) + 's');
          const peek = this.collectApisFromIframe(iframe);
          if (peek.length) {
            this.uiRowStatus = Object.assign({}, this.uiRowStatus, {
              [route.id]: Object.assign({}, this.uiStatusFor(route), {
                discoveredApis: peek,
                phase: 'Settling · ' + peek.length + ' API(s) so far'
              })
            });
            this.cdr.detectChanges();
          }
          if (remaining <= 0) {
            this.clearSettleCountdown();
          }
        }, 250);

        window.setTimeout(() => {
          this.clearSettleCountdown();
          this.setActivity('settle', 'done', settleMs + 'ms');
          this.setActivity('harvest', 'active');
          this.updateUiPhase(route.id, 'Harvesting APIs…');
          try {
            const win = iframe.contentWindow;
            const doc = iframe.contentDocument;
            if (!win || !doc) {
              finish({ok: false, error: 'iframe document unavailable', discoveredApis: []});
              return;
            }
            if (win.location.origin !== this.origin) {
              finish({
                ok: false,
                error: 'Navigated off-origin (likely auth redirect): ' + win.location.href,
                discoveredApis: []
              });
              return;
            }
            const discovered = this.collectApisFromIframe(iframe);
            this.setActivity('harvest', 'done', discovered.length + ' call(s)');
            this.setActivity('check', 'active', 'expected ' + route.expectedApis.length);
            this.updateUiPhase(route.id, 'Checking expectations…');

            const root = doc.querySelector('app-root');
            const hasUi = !!(root && (root as HTMLElement).innerHTML && (root as HTMLElement).innerHTML.length > 20);
            const bodyText = (doc.body && doc.body.innerText) || '';
            const looksLikeError =
              /Cannot GET|Template parse errors|Failed to compile/i.test(bodyText) ||
              /Template parse errors/i.test((root && (root as HTMLElement).innerText) || '');
            const missing = (route.expectedApis || []).filter(exp => !discovered.some(d => d.indexOf(exp) >= 0));
            let error: string | undefined;
            let ok = true;
            if (looksLikeError) {
              ok = false;
              error = 'Page rendered with template/runtime errors';
            } else if (!hasUi) {
              ok = false;
              error = 'app-root empty — likely auth redirect, guard block, or bootstrap failure';
            } else if (
              route.expectedApis.length &&
              missing.length === route.expectedApis.length &&
              !discovered.length
            ) {
              ok = false;
              error = 'No ASR/auth API calls captured after settle (page may still be spinning or blocked)';
            } else if (missing.length) {
              ok = false;
              error = 'Missing expected API(s): ' + missing.join(', ');
            }
            finish({
              ok,
              error,
              discoveredApis: discovered,
              httpErrors: 0
            });
          } catch (e) {
            finish({
              ok: false,
              error: e && (e as Error).message ? (e as Error).message : 'iframe access error',
              discoveredApis: []
            });
          }
        }, settleMs);
      };

      iframe.onerror = () => {
        this.setActivity('load', 'fail', 'iframe failed to load');
        this.updateUiPhase(route.id, 'Load error');
        finish({ok: false, error: 'iframe failed to load', discoveredApis: []});
      };

      document.body.appendChild(iframe);
      this.setActivity('open', 'done', url);
      this.setActivity('load', 'active', 'waiting for onload…');
      this.updateUiPhase(route.id, 'Loading document…');
      iframe.src = url + (url.indexOf('?') >= 0 ? '&' : '?') + '_asrapitest=' + Date.now();
    }).then(() => null);
  }

  private updateUiPhase(id: string, phase: string): void {
    const s = this.uiStatusFor({id} as UiRouteDef);
    if (s.state !== 'running') {
      return;
    }
    this.uiRowStatus = Object.assign({}, this.uiRowStatus, {
      [id]: Object.assign({}, s, {phase})
    });
    this.cdr.detectChanges();
  }

  private markUiRunning(id: string, t0: number, phase?: string): void {
    const s = this.uiStatusFor({id} as UiRouteDef);
    this.uiRowStatus = Object.assign({}, this.uiRowStatus, {
      [id]: Object.assign({}, s, {
        state: 'running' as EndpointRunState,
        startedAt: t0,
        liveSec: 0,
        ok: null,
        error: undefined,
        discoveredApis: [],
        missingExpected: [],
        extraApis: [],
        phase: phase || 'Starting…'
      })
    });
  }

  private collectApisFromIframe(iframe: HTMLIFrameElement): string[] {
    try {
      const win = iframe.contentWindow;
      if (!win) {
        return [];
      }
      const entries = win.performance.getEntriesByType('resource') as PerformanceResourceTiming[];
      const apiNeedle = this.apiBase || '/asr';
      const authNeedle = this.authBase || 'auth';
      const found: string[] = [];
      entries.forEach(e => {
        const name = e.name || '';
        if (name.indexOf(apiNeedle) >= 0 || name.indexOf(authNeedle) >= 0 || /\/ncats_test\/asr\//.test(name)) {
          // normalize to path after host
          let path = name;
          try {
            const u = new URL(name);
            path = u.pathname + u.search;
          } catch (err) {
            /* keep raw */
          }
          if (found.indexOf(path) < 0) {
            found.push(path);
          }
        }
      });
      return found;
    } catch (e) {
      return [];
    }
  }

  private startUiTicker(): void {
    this.stopTicker();
    this.tickTimer = setInterval(() => {
      const now = performance.now();
      let next: {[id: string]: UiRouteRowStatus} | null = null;
      Object.keys(this.uiRowStatus).forEach(id => {
        const s = this.uiRowStatus[id];
        if (s.state === 'running' && s.startedAt != null) {
          if (!next) {
            next = Object.assign({}, this.uiRowStatus);
          }
          next[id] = Object.assign({}, s, {liveSec: (now - s.startedAt) / 1000});
        }
      });
      if (next) {
        this.uiRowStatus = next;
        this.cdr.detectChanges();
      }
    }, 100);
  }

  private recomputeUiSummary(): void {
    const rows = this.uiRoutes.map(r => this.uiStatusFor(r)).filter(s => s.state === 'ok' || s.state === 'fail');
    if (!rows.length) {
      this.uiSummary = {total: 0, ok: 0, fail: 0, avgMs: 0, minMs: 0, maxMs: 0, apisFound: 0};
      return;
    }
    const durations = rows.map(r => r.durationMs || 0);
    const sum = durations.reduce((a, b) => a + b, 0);
    const apisFound = rows.reduce((a, r) => a + (r.discoveredApis ? r.discoveredApis.length : 0), 0);
    this.uiSummary = {
      total: rows.length,
      ok: rows.filter(r => r.ok).length,
      fail: rows.filter(r => !r.ok).length,
      avgMs: Math.round(sum / rows.length),
      minMs: Math.min(...durations),
      maxMs: Math.max(...durations),
      apisFound
    };
  }

  private destroyActiveIframe(): void {
    if (this.activeIframe && this.activeIframe.parentNode) {
      this.activeIframe.parentNode.removeChild(this.activeIframe);
    }
    this.activeIframe = null;
  }

  selectAllGets(checked: boolean): void {
    this.getEndpoints.forEach(e => {
      if (!e.mutating) {
        e.selected = checked;
      }
    });
  }

  onGetSelect(endpoint: ApiEndpointDef, event: Event): void {
    if (endpoint.mutating) {
      endpoint.selected = false;
      return;
    }
    const input = event.target as HTMLInputElement;
    endpoint.selected = !!(input && input.checked);
  }

  selectSafeGets(): void {
    this.getEndpoints.forEach(e => {
      e.selected = !e.mutating && !e.heavy && !e.needsReviewId && !e.needsReviewerId;
    });
  }

  clearResults(): void {
    this.results = [];
    this.resetSummary();
    if (!this.running) {
      this.resetRowStatuses();
    }
  }

  stopRuns(): void {
    this.stop$.next();
    this.running = false;
    this.stopTicker();
    this.clearSettleCountdown();
    this.destroyActiveIframe();
    this.setActivity('done', 'skip', 'Stopped by user');
    this.activityCurrent = 'Stopped';
    Object.keys(this.rowStatus).forEach(id => {
      const s = this.rowStatus[id];
      if (s.state === 'running' || s.state === 'queued') {
        s.state = s.doneCount > 0 ? (s.status != null && s.status >= 200 && s.status < 300 ? 'ok' : 'fail') : 'idle';
        s.startedAt = null;
      }
    });
    Object.keys(this.uiRowStatus).forEach(id => {
      const s = this.uiRowStatus[id];
      if (s.state === 'running' || s.state === 'queued') {
        s.state = s.ok ? 'ok' : s.ok === false ? 'fail' : 'idle';
        s.startedAt = null;
        s.phase = undefined;
      }
    });
    this.cdr.detectChanges();
  }

  runSelectedGets(): void {
    this.executeGets(this.runnableGets);
  }

  runAllSafeGets(): void {
    this.selectSafeGets();
    // also include review-scoped if reviewId present
    const reviewId = (this.form.get('reviewId').value || '').toString().trim();
    if (reviewId) {
      this.getEndpoints.forEach(e => {
        if (!e.mutating && !e.heavy && e.needsReviewId) {
          e.selected = true;
        }
      });
    }
    this.executeGets(this.runnableGets);
  }

  private executeGets(endpoints: ApiEndpointDef[]): void {
    if (this.running || !endpoints.length) {
      return;
    }

    const iterations = Math.max(1, Math.min(50, Number(this.form.get('iterations').value) || 1));
    const concurrency = Math.max(1, Math.min(10, Number(this.form.get('concurrency').value) || 1));
    let reviewId = (this.form.get('reviewId').value || '').toString().trim();
    const reviewerId = (this.form.get('reviewerId').value || '').toString().trim();

    const missingReview = endpoints.some(e => e.needsReviewId) && !reviewId;
    if (missingReview) {
      // Try to resolve from reviews/my once before failing
      this.running = true;
      this.progressDone = 0;
      this.progressTotal = 1;
      this.subs.add(
        this.http.get(`${this.apiBase}/reviews/my`).subscribe({
          next: (data: any) => {
            const first = Array.isArray(data) ? data[0] : null;
            const id = first && (first.id || first.Id || first.ID);
            if (id) {
              reviewId = String(id);
              this.form.patchValue({reviewId});
              this.running = false;
              this.startQueue(endpoints, iterations, concurrency, reviewId, reviewerId);
            } else {
              this.running = false;
              this.pushErrorPrompt({
                title: 'Missing Review ID',
                message:
                  'reviewId is required for some selected endpoints. Enter a Review ID or ensure /reviews/my returns data.',
                source: 'system'
              });
            }
          },
          error: () => {
            this.running = false;
            this.pushErrorPrompt({
              title: 'Could not resolve Review ID',
              message: 'Could not resolve reviewId from /reviews/my. Enter a Review ID manually.',
              source: 'system'
            });
          }
        })
      );
      return;
    }

    if (endpoints.some(e => e.needsReviewerId) && !reviewerId) {
      this.pushErrorPrompt({
        title: 'Missing Reviewer ID',
        message: 'reviewerId is required for some selected endpoints.',
        source: 'system'
      });
      return;
    }

    this.startQueue(endpoints, iterations, concurrency, reviewId, reviewerId);
  }

  private startQueue(
    endpoints: ApiEndpointDef[],
    iterations: number,
    concurrency: number,
    reviewId: string,
    reviewerId: string
  ): void {
    this.stop$.next();
    this.stop$ = new Subject<void>();
    this.clearResults();
    this.running = true;
    this.initRowStatusesForRun(endpoints, iterations);
    this.startTicker();
    this.resetActivity('API GET run', [
      {id: 'prepare', label: 'Queue selected GET endpoints'},
      {id: 'request', label: 'Send HTTP request(s)'},
      {id: 'wait', label: 'Wait for response (live elapsed in table)'},
      {id: 'record', label: 'Record status / bytes / errors'},
      {id: 'done', label: 'GET run complete'}
    ]);
    this.setActivity('prepare', 'done', endpoints.length + ' endpoint(s) × ' + iterations);

    const jobs: Array<{endpoint: ApiEndpointDef; iteration: number}> = [];
    for (let i = 1; i <= iterations; i++) {
      endpoints.forEach(endpoint => jobs.push({endpoint, iteration: i}));
    }
    this.progressTotal = jobs.length;
    this.progressDone = 0;
    this.setActivity('request', 'active', '0 / ' + jobs.length);

    this.subs.add(
      from(jobs)
        .pipe(
          mergeMap(
            job =>
              this.runOne(job.endpoint, job.iteration, reviewId, reviewerId).pipe(
                tap(result => {
                  this.results = [result, ...this.results];
                  this.progressDone += 1;
                  this.applyResultToRow(result);
                  this.recomputeSummary();
                  this.setActivity(
                    'request',
                    'active',
                    this.progressDone + ' / ' + this.progressTotal + ' · last: ' + result.name
                  );
                  this.setActivity(
                    'wait',
                    result.ok ? 'done' : 'fail',
                    result.durationMs + 'ms' + (result.error ? ' · ' + result.error : '')
                  );
                  this.setActivity('record', 'active', result.ok ? 'HTTP ' + result.status : result.error || 'fail');
                  if (!result.ok) {
                    this.pushErrorPrompt({
                      title: 'API GET failed: ' + result.name,
                      message: result.error || 'Request failed with status ' + (result.status || 'unknown'),
                      details: result.method + ' ' + result.url + '\nDuration: ' + result.durationMs + 'ms',
                      source: 'api',
                      path: result.url
                    });
                  }
                  this.cdr.detectChanges();
                }),
                catchError(() => of(null))
              ),
            concurrency
          ),
          takeUntil(this.stop$),
          finalize(() => {
            this.running = false;
            this.stopTicker();
            this.recomputeSummary();
            this.setActivity('record', 'done');
            this.setActivity(
              'done',
              this.summary.fail ? 'fail' : 'done',
              this.summary.ok + ' ok / ' + this.summary.fail + ' fail'
            );
            this.activityCurrent = this.summary.fail
              ? 'GET run finished with failures — see error modal'
              : 'GET run finished';
            this.cdr.detectChanges();
          })
        )
        .subscribe()
    );
  }

  private runOne(endpoint: ApiEndpointDef, iteration: number, reviewId: string, reviewerId: string) {
    const url = this.resolveUrl(endpoint, reviewId, reviewerId);
    const startedAt = new Date().toISOString();
    const t0 = performance.now();
    this.markRowRunning(endpoint.id, t0);
    this.setActivity('wait', 'active', endpoint.name + ' · waiting on network…');
    this.cdr.detectChanges();

    return this.http
      .request(endpoint.method, url, {
        observe: 'response',
        responseType: 'text'
      })
      .pipe(
        map((res: HttpResponse<string>) => this.toResult(endpoint, url, iteration, startedAt, t0, res)),
        catchError((err: HttpErrorResponse) => of(this.toErrorResult(endpoint, url, iteration, startedAt, t0, err)))
      );
  }

  private resetRowStatuses(): void {
    const next: {[id: string]: EndpointRowStatus} = {};
    this.getEndpoints.concat(this.postEndpoints).forEach(e => {
      next[e.id] = {
        state: 'idle',
        liveSec: 0,
        durationMs: null,
        status: null,
        doneCount: 0,
        totalCount: 0,
        startedAt: null,
        label: undefined
      };
    });
    this.rowStatus = next;
  }

  private initRowStatusesForRun(endpoints: ApiEndpointDef[], iterations: number): void {
    const next: {[id: string]: EndpointRowStatus} = Object.assign({}, this.rowStatus);
    endpoints.forEach(e => {
      next[e.id] = {
        state: 'queued',
        liveSec: 0,
        durationMs: null,
        status: null,
        doneCount: 0,
        totalCount: iterations,
        startedAt: null,
        label: undefined
      };
    });
    this.rowStatus = next;
  }

  private markRowRunning(id: string, t0: number): void {
    const s = this.rowStatus[id];
    if (!s) {
      return;
    }
    this.rowStatus = Object.assign({}, this.rowStatus, {
      [id]: Object.assign({}, s, {
        state: 'running' as EndpointRunState,
        startedAt: t0,
        liveSec: 0,
        label: undefined
      })
    });
  }

  private applyResultToRow(result: ApiRunResult): void {
    const s = this.rowStatus[result.id];
    if (!s) {
      return;
    }
    const doneCount = s.doneCount + 1;
    this.rowStatus = Object.assign({}, this.rowStatus, {
      [result.id]: Object.assign({}, s, {
        doneCount,
        durationMs: result.durationMs,
        liveSec: result.durationMs / 1000,
        status: result.status,
        error: result.error,
        startedAt: null,
        label: result.label,
        bytes: result.bytes,
        state: (result.ok ? 'ok' : 'fail') as EndpointRunState
      })
    });
  }

  private executePostDryRuns(endpoints: ApiEndpointDef[]): void {
    if (this.running || !endpoints.length) {
      return;
    }

    const iterations = Math.max(1, Math.min(50, Number(this.form.get('postIterations').value) || 1));
    const concurrency = Math.max(1, Math.min(10, Number(this.form.get('postConcurrency').value) || 1));
    const reviewId = (this.form.get('reviewId').value || '').toString().trim();
    const reviewerId = (this.form.get('reviewerId').value || '').toString().trim();

    this.stop$.next();
    this.stop$ = new Subject<void>();
    this.results = [];
    this.lastPostDryPreview = null;
    this.resetSummary();
    this.running = true;
    this.initRowStatusesForRun(endpoints, iterations);
    this.startTicker();
    this.resetActivity('POST dry-run', [
      {id: 'prepare', label: 'Build dry-run payloads (no network)'},
      {id: 'serialize', label: 'Serialize sample JSON bodies'},
      {id: 'record', label: 'Record size / preview'},
      {id: 'done', label: 'POST dry-run complete'}
    ]);
    this.setActivity('prepare', 'active', endpoints.length + ' endpoint(s)');
    this.cdr.detectChanges();

    const jobs: Array<{endpoint: ApiEndpointDef; iteration: number}> = [];
    for (let i = 1; i <= iterations; i++) {
      endpoints.forEach(endpoint => jobs.push({endpoint, iteration: i}));
    }
    this.progressTotal = jobs.length;
    this.progressDone = 0;
    this.setActivity('prepare', 'done');
    this.setActivity('serialize', 'active');

    this.subs.add(
      from(jobs)
        .pipe(
          mergeMap(
            job =>
              this.runOnePostDry(job.endpoint, job.iteration, reviewId, reviewerId).pipe(
                tap(result => {
                  this.results = [result, ...this.results];
                  this.progressDone += 1;
                  this.applyResultToRow(result);
                  if (result.label === 'DRY' || result.label === 'DRY-ERR') {
                    this.lastPostDryPreview = {
                      name: result.name,
                      url: result.url,
                      bytes: result.bytes,
                      durationMs: result.durationMs,
                      body: result.preview || result.error || ''
                    };
                  }
                  this.recomputeSummary();
                  this.setActivity(
                    'serialize',
                    'active',
                    this.progressDone + ' / ' + this.progressTotal + ' · ' + result.name
                  );
                  if (!result.ok) {
                    this.pushErrorPrompt({
                      title: 'POST dry-run failed: ' + result.name,
                      message: result.error || 'Dry-run payload build failed',
                      details: result.url,
                      source: 'api',
                      path: result.url
                    });
                  }
                  this.cdr.detectChanges();
                }),
                catchError(() => of(null))
              ),
            concurrency
          ),
          takeUntil(this.stop$),
          finalize(() => {
            this.running = false;
            this.stopTicker();
            this.recomputeSummary();
            this.setActivity('record', 'done');
            this.setActivity(
              'done',
              this.summary.fail ? 'fail' : 'done',
              this.summary.ok + ' ok / ' + this.summary.fail + ' fail'
            );
            this.cdr.detectChanges();
          })
        )
        .subscribe()
    );
  }

  private runOnePostDry(endpoint: ApiEndpointDef, iteration: number, reviewId: string, reviewerId: string) {
    const startedAt = new Date().toISOString();
    const t0 = performance.now();
    this.markRowRunning(endpoint.id, t0);
    this.cdr.detectChanges();

    const url = this.resolvePostUrl(endpoint, reviewId, reviewerId);
    const body = this.buildPostDryBody(endpoint, reviewId, reviewerId);
    let bytes = 0;
    let error: string | undefined;
    let ok = true;
    let preview = '';
    try {
      const serialized = JSON.stringify(body, null, 2);
      bytes = serialized ? serialized.length : 0;
      preview = serialized || '';
      if (body == null) {
        ok = false;
        error = 'No sample body defined for dry-run.';
      }
    } catch (e) {
      ok = false;
      error = e && (e as Error).message ? (e as Error).message : 'Failed to serialize sample body';
    }

    const buildMs = Math.max(1, Math.round(performance.now() - t0));
    // Keep row in "running" long enough for live ms to be visible (min ~250ms).
    const waitMs = Math.max(0, 250 - buildMs);

    return timer(waitMs).pipe(
      map(() => {
        const result: ApiRunResult = {
          id: endpoint.id,
          name: endpoint.name,
          method: 'POST',
          url,
          status: null,
          ok,
          durationMs: buildMs,
          bytes,
          error,
          iteration,
          startedAt,
          label: ok ? 'DRY' : 'DRY-ERR',
          preview: preview.slice(0, 4000)
        };
        return result;
      })
    );
  }

  private resolvePostUrl(endpoint: ApiEndpointDef, reviewId: string, reviewerId: string): string {
    let path = (endpoint.pathTemplate || '')
      .replace('{reviewId}', encodeURIComponent(reviewId || '0'))
      .replace('{reviewerId}', encodeURIComponent(reviewerId || '0'))
      .replace('{listName}', 'Programs')
      .replace('{tenant}', encodeURIComponent(this.tenant));

    const [pathname, query] = path.split('?');
    const encodedPath = pathname
      .split('/')
      .map(seg => (seg && /[^A-Za-z0-9._~-]/.test(seg) ? encodeURIComponent(seg) : seg))
      .join('/');
    path = query ? `${encodedPath}?${query}` : encodedPath;
    return `${this.apiBase}/${path.replace(/^\//, '')}`;
  }

  private buildPostDryBody(endpoint: ApiEndpointDef, reviewId: string, reviewerId: string): any {
    const stamp = {
      mode: 'dry-run',
      notSent: true,
      reviewId: reviewId || null,
      reviewerId: reviewerId || null,
      at: new Date().toISOString()
    };
    if (endpoint.sampleBody != null) {
      const body = JSON.parse(JSON.stringify(endpoint.sampleBody));
      if (body && typeof body === 'object' && !Array.isArray(body)) {
        if (body.reviewId === '{reviewId}') {
          body.reviewId = reviewId || '{reviewId}';
        }
        body.__asrapitest = stamp;
        return body;
      }
      return {items: body, __asrapitest: stamp};
    }
    return {__asrapitest: stamp};
  }

  private startTicker(): void {
    this.stopTicker();
    this.tickTimer = setInterval(() => {
      const now = performance.now();
      let next: {[id: string]: EndpointRowStatus} | null = null;
      Object.keys(this.rowStatus).forEach(id => {
        const s = this.rowStatus[id];
        if (s.state === 'running' && s.startedAt != null) {
          if (!next) {
            next = Object.assign({}, this.rowStatus);
          }
          next[id] = Object.assign({}, s, {liveSec: (now - s.startedAt) / 1000});
        }
      });
      if (next) {
        this.rowStatus = next;
        this.cdr.detectChanges();
      }
    }, 100);
  }

  private stopTicker(): void {
    if (this.tickTimer != null) {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
  }

  private toResult(
    endpoint: ApiEndpointDef,
    url: string,
    iteration: number,
    startedAt: string,
    t0: number,
    res: HttpResponse<string>
  ): ApiRunResult {
    const body = res.body || '';
    return {
      id: endpoint.id,
      name: endpoint.name,
      method: endpoint.method,
      url,
      status: res.status,
      ok: res.status >= 200 && res.status < 300,
      durationMs: Math.round(performance.now() - t0),
      bytes: typeof body === 'string' ? body.length : 0,
      iteration,
      startedAt
    };
  }

  private toErrorResult(
    endpoint: ApiEndpointDef,
    url: string,
    iteration: number,
    startedAt: string,
    t0: number,
    err: HttpErrorResponse
  ): ApiRunResult {
    const message = this.extractHttpErrorMessage(err, url);
    return {
      id: endpoint.id,
      name: endpoint.name,
      method: endpoint.method,
      url,
      status: err.status || null,
      ok: false,
      durationMs: Math.round(performance.now() - t0),
      bytes: message.length,
      error: message.slice(0, 500),
      iteration,
      startedAt
    };
  }

  private extractHttpErrorMessage(err: HttpErrorResponse, url: string): string {
    if (!err) {
      return 'Unknown request failure';
    }
    // status 0 usually means browser could not reach the API (down / CORS / TLS)
    if (err.status === 0) {
      return (
        'Connection failed (status 0) — is asr-services running at ' +
        (this.apiBase || url) +
        '? ' +
        (err.message || 'net::ERR_CONNECTION_REFUSED / Unknown Error')
      );
    }
    const payload = err.error;
    if (typeof payload === 'string' && payload.trim()) {
      return 'HTTP ' + err.status + ': ' + payload.trim().slice(0, 400);
    }
    if (payload && typeof payload === 'object') {
      const nested = (payload.error && (payload.error.message || payload.error)) || payload.message || payload.name;
      if (nested) {
        const text = typeof nested === 'string' ? nested : JSON.stringify(nested);
        return 'HTTP ' + err.status + ': ' + text.slice(0, 400);
      }
    }
    if (err.message) {
      return 'HTTP ' + (err.status || '?') + ': ' + err.message;
    }
    return 'HTTP ' + (err.status || '?') + ' ' + (err.statusText || 'Request failed');
  }

  private resolveUrl(endpoint: ApiEndpointDef, reviewId: string, reviewerId: string): string {
    let path = endpoint.pathTemplate
      .replace('{reviewId}', encodeURIComponent(reviewId))
      .replace('{reviewerId}', encodeURIComponent(reviewerId))
      .replace('{tenant}', encodeURIComponent(this.tenant));

    // Encode list path segments that contain spaces, keep query string intact
    const [pathname, query] = path.split('?');
    const encodedPath = pathname
      .split('/')
      .map(seg => (seg && /[^A-Za-z0-9._~-]/.test(seg) ? encodeURIComponent(seg) : seg))
      .join('/');
    path = query ? `${encodedPath}?${query}` : encodedPath;

    if (endpoint.authHost) {
      return `${this.authBase}/${path.replace(/^\//, '')}`;
    }
    return `${this.apiBase}/${path.replace(/^\//, '')}`;
  }

  private recomputeSummary(): void {
    const list = this.results;
    const total = list.length;
    if (!total) {
      this.resetSummary();
      return;
    }
    const ok = list.filter(r => r.ok).length;
    const durations = list.map(r => r.durationMs);
    const sum = durations.reduce((a, b) => a + b, 0);
    this.summary = {
      total,
      ok,
      fail: total - ok,
      avgMs: Math.round(sum / total),
      minMs: Math.min(...durations),
      maxMs: Math.max(...durations)
    };
  }

  private resetSummary(): void {
    this.summary = {total: 0, ok: 0, fail: 0, avgMs: 0, minMs: 0, maxMs: 0};
    this.progressDone = 0;
    this.progressTotal = 0;
  }

  private buildGetCatalog(): ApiEndpointDef[] {
    const defs: ApiEndpointDef[] = [
      {
        id: 'users-currentUser',
        name: 'Current user',
        method: 'GET',
        group: 'Users',
        pathTemplate: 'users/currentUser',
        description: 'Session user + roles (home bootstrap)',
        selected: true
      },
      {
        id: 'reviews-my',
        name: 'My reviews',
        method: 'GET',
        group: 'Reviews',
        pathTemplate: 'reviews/my',
        description: 'Reviews assigned to / visible for current user',
        selected: true
      },
      {
        id: 'users-prgl',
        name: 'Users by role (PRGL)',
        method: 'GET',
        group: 'Users',
        pathTemplate: 'users/prgl',
        description: 'Program lead users',
        selected: false
      },
      {
        id: 'users-reviewer',
        name: 'Users by role (REVIEWER)',
        method: 'GET',
        group: 'Users',
        pathTemplate: 'users/reviewer',
        description: 'Reviewer users',
        selected: false
      },
      {
        id: 'list-Programs',
        name: 'Programs',
        method: 'GET',
        group: 'Reference lists',
        pathTemplate: 'Programs',
        description: 'Generic list findAll',
        selected: true
      },
      {
        id: 'list-SubPrograms',
        name: 'Sub Programs',
        method: 'GET',
        group: 'Reference lists',
        pathTemplate: 'Sub Programs',
        description: 'Generic list findAll',
        selected: true
      },
      {
        id: 'list-Expertise',
        name: 'Expertise',
        method: 'GET',
        group: 'Reference lists',
        pathTemplate: 'Expertise',
        description: 'Generic list findAll',
        selected: true
      },
      {
        id: 'list-DocumentTypes',
        name: 'DocumentTypes',
        method: 'GET',
        group: 'Reference lists',
        pathTemplate: 'DocumentTypes',
        description: 'Generic list findAll',
        selected: true
      },
      {
        id: 'list-DocumentCategories',
        name: 'DocumentCategories',
        method: 'GET',
        group: 'Reference lists',
        pathTemplate: 'DocumentCategories',
        description: 'Generic list findAll',
        selected: true
      },
      {
        id: 'list-EmploymentCategories',
        name: 'EmploymentCategories',
        method: 'GET',
        group: 'Reference lists',
        pathTemplate: 'EmploymentCategories',
        description: 'Generic list findAll',
        selected: false
      },
      {
        id: 'list-DPIOverview',
        name: 'DPIOverview',
        method: 'GET',
        group: 'Home / OSD',
        pathTemplate: 'DPIOverview',
        description: 'Home overview content',
        selected: true
      },
      {
        id: 'list-SurveySettings',
        name: 'Survey Settings',
        method: 'GET',
        group: 'Surveys',
        pathTemplate: 'Survey Settings',
        description: 'Survey settings list',
        selected: true
      },
      {
        id: 'list-LeadSurveyUsers',
        name: 'Lead Survey Users',
        method: 'GET',
        group: 'Surveys',
        pathTemplate: 'Lead Survey Users',
        description: 'Lead survey users list',
        selected: false
      },
      {
        id: 'list-Roles',
        name: 'Roles',
        method: 'GET',
        group: 'Users',
        pathTemplate: 'Roles',
        description: 'Roles list',
        selected: false
      },
      {
        id: 'list-Users',
        name: 'Users',
        method: 'GET',
        group: 'Users',
        pathTemplate: 'Users',
        description: 'Users SharePoint list',
        selected: false
      },
      {
        id: 'auth-me',
        name: 'Auth me',
        method: 'GET',
        group: 'Auth',
        pathTemplate: 'auth/{tenant}/me',
        description: 'Auth service current identity',
        authHost: true,
        selected: true
      },
      {
        id: 'milestones-byReview',
        name: 'Milestones by review',
        method: 'GET',
        group: 'Milestones',
        pathTemplate: 'milestones/byReviewId/{reviewId}',
        description: 'Read milestones for a review',
        needsReviewId: true,
        selected: false
      },
      {
        id: 'reviewers-byReview',
        name: 'Reviewers by review',
        method: 'GET',
        group: 'Reviewers',
        pathTemplate: 'reviewers/byReviewId/{reviewId}',
        description: 'Reviewers assigned to a review',
        needsReviewId: true,
        selected: false
      },
      {
        id: 'uploadedDocs-byReview',
        name: 'Uploaded docs by review',
        method: 'GET',
        group: 'Materials',
        pathTemplate: 'uploadedDocs/byReview/{reviewId}',
        description: 'Materials / uploaded docs',
        needsReviewId: true,
        selected: false
      },
      {
        id: 'quadStaff-byReview',
        name: 'Quad staff by review',
        method: 'GET',
        group: 'Program staff',
        pathTemplate: 'quadStaffs/byReview/{reviewId}',
        description: 'Quad staff for a review',
        needsReviewId: true,
        selected: false
      },
      {
        id: 'reviews-byId',
        name: 'Review by id',
        method: 'GET',
        group: 'Reviews',
        pathTemplate: 'Reviews/{reviewId}',
        description: 'Generic findById on Reviews list',
        needsReviewId: true,
        selected: false
      },
      {
        id: 'ms-plLockAvailable',
        name: 'PL lock available',
        method: 'GET',
        group: 'Milestones (read flags)',
        pathTemplate: 'milestones/plLockAvailable/{reviewId}',
        description: 'Read-only lock availability flag',
        needsReviewId: true,
        selected: false
      },
      {
        id: 'ms-sdLockAvailable',
        name: 'SD lock available',
        method: 'GET',
        group: 'Milestones (read flags)',
        pathTemplate: 'milestones/sdLockAvailable/{reviewId}',
        description: 'Read-only lock availability flag',
        needsReviewId: true,
        selected: false
      },
      {
        id: 'ms-bdLockAvailable',
        name: 'BD lock available',
        method: 'GET',
        group: 'Milestones (read flags)',
        pathTemplate: 'milestones/bdLockAvailable/{reviewId}',
        description: 'Read-only lock availability flag',
        needsReviewId: true,
        selected: false
      },
      {
        id: 'ms-plSvmLockAvailable',
        name: 'PL SVM lock available',
        method: 'GET',
        group: 'Milestones (read flags)',
        pathTemplate: 'milestones/plSvmLockAvailable/{reviewId}',
        description: 'Read-only site-visit materials lock flag',
        needsReviewId: true,
        selected: false
      },
      {
        id: 'ms-sdSvmLockAvailable',
        name: 'SD SVM lock available',
        method: 'GET',
        group: 'Milestones (read flags)',
        pathTemplate: 'milestones/sdSvmLockAvailable/{reviewId}',
        description: 'Read-only site-visit materials lock flag',
        needsReviewId: true,
        selected: false
      },
      {
        id: 'ms-bdSvmLockAvailable',
        name: 'BD SVM lock available',
        method: 'GET',
        group: 'Milestones (read flags)',
        pathTemplate: 'milestones/bdSvmLockAvailable/{reviewId}',
        description: 'Read-only site-visit materials lock flag',
        needsReviewId: true,
        selected: false
      },
      {
        id: 'ms-quadReportsLockAvailable',
        name: 'Quad reports lock available',
        method: 'GET',
        group: 'Milestones (read flags)',
        pathTemplate: 'milestones/quadReportsLockAvailable/{reviewId}',
        description: 'Read-only quad reports lock flag',
        needsReviewId: true,
        selected: false
      },
      {
        id: 'reviewers-checkInfo',
        name: 'Reviewer checkInfo',
        method: 'GET',
        group: 'Reviewers',
        pathTemplate: 'reviewers/{reviewerId}/checkInfo',
        description: 'Reviewer info check',
        needsReviewerId: true,
        selected: false
      },
      {
        id: 'programReports-downloadAll',
        name: 'Program reports downloadAll',
        method: 'GET',
        group: 'Downloads (heavy)',
        pathTemplate: 'programReports/downloadAll/{reviewId}',
        description: 'Binary zip download — opt-in only',
        needsReviewId: true,
        heavy: true,
        selected: false
      },
      // Mutating GETs — listed for awareness, never selectable for auto-run
      {
        id: 'ms-initiateReview',
        name: 'initiateReview (MUTATING)',
        method: 'GET',
        group: 'Excluded — mutating GETs',
        pathTemplate: 'milestones/initiateReview/{reviewId}',
        description: 'Mutates milestone state — excluded from harness runs',
        needsReviewId: true,
        mutating: true,
        selected: false
      },
      {
        id: 'ms-lockAgenda',
        name: 'lockAgenda (MUTATING)',
        method: 'GET',
        group: 'Excluded — mutating GETs',
        pathTemplate: 'milestones/lockAgenda/{reviewId}',
        description: 'Mutates milestone state — excluded from harness runs',
        needsReviewId: true,
        mutating: true,
        selected: false
      },
      {
        id: 'ms-plLockComplete',
        name: 'plLockMaterials/complete (MUTATING)',
        method: 'GET',
        group: 'Excluded — mutating GETs',
        pathTemplate: 'milestones/plLockMaterials/complete/{reviewId}',
        description: 'Locks materials — excluded from harness runs',
        needsReviewId: true,
        mutating: true,
        selected: false
      },
      {
        id: 'ms-sdLockComplete',
        name: 'sdLockMaterials/complete (MUTATING)',
        method: 'GET',
        group: 'Excluded — mutating GETs',
        pathTemplate: 'milestones/sdLockMaterials/complete/{reviewId}',
        description: 'Locks materials — excluded from harness runs',
        needsReviewId: true,
        mutating: true,
        selected: false
      }
    ];
    return defs;
  }

  private buildPostCatalog(): ApiEndpointDef[] {
    return [
      {
        id: 'reviews-create',
        name: 'Create review',
        method: 'POST',
        group: 'Reviews',
        pathTemplate: 'reviews/create',
        description: 'Would create a review — dry-run only (never sent)',
        postSafety: 'mutating',
        selected: true,
        sampleBody: {
          title: '__asrapitest_dry_run__',
          year: 2099,
          status: 'OPEN'
        }
      },
      {
        id: 'generic-create',
        name: 'Generic list create',
        method: 'POST',
        group: 'Generic',
        pathTemplate: '{listName}',
        description: 'Would create a SharePoint list item — dry-run only',
        postSafety: 'mutating',
        selected: true,
        sampleBody: {Title: '__asrapitest_dry_run__'}
      },
      {
        id: 'generic-batch',
        name: 'Generic list batch',
        method: 'POST',
        group: 'Generic',
        pathTemplate: '{listName}/batch',
        description: 'Would batch-create items — dry-run only',
        postSafety: 'mutating',
        selected: true,
        sampleBody: [{Title: '__asrapitest_dry_run_1__'}, {Title: '__asrapitest_dry_run_2__'}]
      },
      {
        id: 'generic-upload',
        name: 'Generic upload',
        method: 'POST',
        group: 'Generic',
        pathTemplate: '{listName}/upload',
        description: 'Would upload a file — dry-run only (no multipart sent)',
        postSafety: 'mutating',
        selected: true,
        sampleBody: {
          fileName: '__asrapitest_dry_run__.txt',
          contentType: 'text/plain',
          note: 'multipart omitted in dry-run'
        }
      },
      {
        id: 'uploadedDocs-addDoc',
        name: 'Add uploaded doc',
        method: 'POST',
        group: 'Materials',
        pathTemplate: 'uploadedDocs/addDoc',
        description: 'Would add a document — dry-run only',
        postSafety: 'mutating',
        selected: true,
        sampleBody: {
          reviewId: '{reviewId}',
          fileName: '__asrapitest_dry_run__.pdf',
          title: '__asrapitest_dry_run__'
        }
      },
      {
        id: 'uploadedDocs-addCv',
        name: 'Add CV',
        method: 'POST',
        group: 'Materials',
        pathTemplate: 'uploadedDocs/addCv',
        description: 'Would add a CV — dry-run only',
        postSafety: 'mutating',
        selected: true,
        sampleBody: {
          reviewId: '{reviewId}',
          scientistId: '__dry_run__',
          fileName: '__asrapitest_dry_run_cv__.pdf'
        }
      },
      {
        id: 'quadStaff-add',
        name: 'Add quad staff',
        method: 'POST',
        group: 'Program staff',
        pathTemplate: 'quadStaffs/addStaff',
        description: 'Would add staff — dry-run only',
        postSafety: 'mutating',
        selected: true,
        sampleBody: {
          reviewId: '{reviewId}',
          staff: [{value: '__asrapitest_dry_run@example.invalid'}]
        }
      },
      {
        id: 'externalUser-authorize',
        name: 'Authorize external user',
        method: 'POST',
        group: 'Users',
        pathTemplate: 'externalUser/authorize',
        description: 'Would authorize an external user — dry-run only',
        postSafety: 'mutating',
        selected: true,
        sampleBody: {
          email: '__asrapitest_dry_run@example.invalid',
          displayName: 'ASR API Test Dry Run'
        }
      },
      {
        id: 'programTeamSurvey-download',
        name: 'Program team survey download',
        method: 'POST',
        group: 'Surveys',
        pathTemplate: 'programTeamSurvey/download',
        description: 'PDF export POST — dry-run builds payload only (no file generated)',
        postSafety: 'download',
        selected: true,
        sampleBody: {
          year: 2099,
          title: '__asrapitest_dry_run__',
          tabs: []
        }
      },
      {
        id: 'branchSurvey-download',
        name: 'Branch survey download',
        method: 'POST',
        group: 'Surveys',
        pathTemplate: 'branchSurvey/download',
        description: 'PDF export POST — dry-run builds payload only (no file generated)',
        postSafety: 'download',
        selected: true,
        sampleBody: {
          year: 2099,
          title: '__asrapitest_dry_run__',
          tabs: []
        }
      }
    ];
  }

  groups(endpoints: ApiEndpointDef[]): string[] {
    return Array.from(new Set(endpoints.map(e => e.group)));
  }

  endpointsInGroup(endpoints: ApiEndpointDef[], group: string): ApiEndpointDef[] {
    return endpoints.filter(e => e.group === group);
  }

  private buildUiRouteCatalog(): UiRouteDef[] {
    // Mirrors ngx-asr-routing.module.ts (lazy `asr` children) + this harness route.
    return [
      {
        id: 'ui-home',
        title: 'Home',
        group: 'Shell',
        pathTemplate: 'asr/home',
        description: 'Landing / role home',
        expectedApis: ['users/currentUser', 'reviews/my', 'DPIOverview', 'Survey%20Settings', 'Lead%20Survey%20Users'],
        selected: true
      },
      {
        id: 'ui-asrapitest',
        title: 'API / UI test harness',
        group: 'Shell',
        pathTemplate: 'asrapitest',
        description: 'This page (self)',
        expectedApis: [],
        selected: false
      },
      {
        id: 'ui-virtual-tours',
        title: 'Virtual tours',
        group: 'Surveys & forms',
        pathTemplate: 'asr/virtualTours',
        description: 'Bias / virtual tour agreement',
        expectedApis: ['users/currentUser', 'Agreements', 'VirtualTours'],
        selected: true
      },
      {
        id: 'ui-program-lead-survey',
        title: 'Program lead survey',
        group: 'Surveys & forms',
        pathTemplate: 'asr/programLeadSurvey/:programName/:programLeadId',
        description: 'Program team survey form',
        needsProgramParams: true,
        expectedApis: ['users/currentUser', 'Survey%20Settings', 'Agreements', 'Lead%20Survey'],
        selected: true
      },
      {
        id: 'ui-branch-survey',
        title: 'Branch survey',
        group: 'Surveys & forms',
        pathTemplate: 'asr/branchSurvey',
        description: 'Branch survey form',
        expectedApis: ['users/currentUser', 'Survey%20Settings', 'Agreements', 'Branch%20Survey'],
        selected: true
      },
      {
        id: 'ui-survey-dashboard',
        title: 'Survey dashboard',
        group: 'Surveys & forms',
        pathTemplate: 'asr/surveyDashboard',
        description: 'Survey admin dashboard',
        expectedApis: ['users/currentUser', 'Survey%20Settings', 'Lead%20Survey', 'Branch%20Survey'],
        selected: true
      },
      {
        id: 'ui-lead-survey-users',
        title: 'Lead survey users dashboard',
        group: 'Surveys & forms',
        pathTemplate: 'asr/leadSurveyUsersDashboard',
        description: 'Lead survey users admin',
        expectedApis: ['users/currentUser', 'Lead%20Survey%20Users'],
        selected: true
      },
      {
        id: 'ui-survey-settings',
        title: 'Survey settings dashboard',
        group: 'Surveys & forms',
        pathTemplate: 'asr/surveySettingsDashboard',
        description: 'Survey settings admin',
        expectedApis: ['users/currentUser', 'Survey%20Settings'],
        selected: true
      },
      {
        id: 'ui-user-dashboard',
        title: 'User dashboard',
        group: 'Users',
        pathTemplate: 'asr/userDashboard',
        description: 'Users admin list',
        expectedApis: ['users/currentUser', 'Users', 'Roles', 'User_Roles'],
        selected: true
      },
      {
        id: 'ui-user-dashboard-id',
        title: 'User dashboard (by id)',
        group: 'Users',
        pathTemplate: 'asr/userDashboard/:userId',
        description: 'Single user admin',
        needsUserId: true,
        expectedApis: ['users/currentUser', 'Users'],
        selected: true
      },
      {
        id: 'ui-osd-reviews',
        title: 'OSD reviews list',
        group: 'OSD',
        pathTemplate: 'asr/osd/reviews',
        description: 'Open/closed ASR reviews (OSD)',
        expectedApis: ['users/currentUser', 'reviews/my', 'Survey%20Settings', 'Lead%20Survey%20Users'],
        selected: true
      },
      {
        id: 'ui-osd-overview',
        title: 'OSD review overview',
        group: 'OSD · review',
        pathTemplate: 'asr/osd/review/:reviewId/overview',
        description: 'Review overview',
        needsReviewId: true,
        expectedApis: ['users/currentUser', 'Reviews/', 'milestones/byReviewId', 'reviewers/byReviewId'],
        selected: true
      },
      {
        id: 'ui-osd-milestones',
        title: 'OSD review milestones',
        group: 'OSD · review',
        pathTemplate: 'asr/osd/review/:reviewId/milestones',
        description: 'Milestones timeline',
        needsReviewId: true,
        expectedApis: ['users/currentUser', 'milestones/byReviewId'],
        selected: true
      },
      {
        id: 'ui-osd-materials',
        title: 'OSD review materials',
        group: 'OSD · review',
        pathTemplate: 'asr/osd/review/:reviewId/materials',
        description: 'Materials / uploaded docs',
        needsReviewId: true,
        expectedApis: ['users/currentUser', 'uploadedDocs/byReview', 'DocumentTypes', 'DocumentCategories'],
        selected: true
      },
      {
        id: 'ui-osd-program-staff',
        title: 'OSD program staff',
        group: 'OSD · review',
        pathTemplate: 'asr/osd/review/:reviewId/programStaff',
        description: 'Quad review staff',
        needsReviewId: true,
        expectedApis: ['users/currentUser', 'quadStaffs/byReview'],
        selected: true
      },
      {
        id: 'ui-osd-reviewers',
        title: 'OSD reviewers',
        group: 'OSD · review',
        pathTemplate: 'asr/osd/review/:reviewId/reviewers',
        description: 'Reviewer assignments',
        needsReviewId: true,
        expectedApis: ['users/currentUser', 'reviewers/byReviewId'],
        selected: true
      },
      {
        id: 'ui-osd-reports',
        title: 'OSD reports',
        group: 'OSD · review',
        pathTemplate: 'asr/osd/review/:reviewId/reports',
        description: 'Program / critique reports',
        needsReviewId: true,
        expectedApis: ['users/currentUser', 'ProgramReports', 'CritiqueSheets'],
        selected: true
      },
      {
        id: 'ui-osd-critique-item',
        title: 'OSD critique sheet (item)',
        group: 'OSD · review',
        pathTemplate: 'asr/osd/review/:reviewId/critiqueSheet/:itemId',
        description: 'Single critique sheet',
        needsReviewId: true,
        needsItemId: true,
        expectedApis: ['users/currentUser', 'CritiqueSheets'],
        selected: false
      },
      {
        id: 'ui-osd-program-report-item',
        title: 'OSD program report (item)',
        group: 'OSD · review',
        pathTemplate: 'asr/osd/review/:reviewId/programReport/:itemId',
        description: 'Single program report',
        needsReviewId: true,
        needsItemId: true,
        expectedApis: ['users/currentUser', 'ProgramReports'],
        selected: false
      },
      {
        id: 'ui-reviewer-reviews',
        title: 'Reviewer reviews list',
        group: 'Reviewer',
        pathTemplate: 'asr/reviewer/reviews',
        description: 'Reviewer-facing reviews',
        expectedApis: ['users/currentUser', 'reviews/my'],
        selected: true
      },
      {
        id: 'ui-reviewer-dashboard',
        title: 'Reviewer dashboard',
        group: 'Reviewer · review',
        pathTemplate: 'asr/reviewer/review/:reviewId/dashboard',
        description: 'Reviewer dashboard for a review',
        needsReviewId: true,
        expectedApis: ['users/currentUser', 'Reviews/', 'reviewers/byReviewId'],
        selected: true
      },
      {
        id: 'ui-reviewer-materials',
        title: 'Reviewer materials',
        group: 'Reviewer · review',
        pathTemplate: 'asr/reviewer/review/:reviewId/materials',
        description: 'Reviewer materials view',
        needsReviewId: true,
        expectedApis: ['users/currentUser', 'uploadedDocs/byReview'],
        selected: true
      },
      {
        id: 'ui-reviewer-reviewers',
        title: 'Reviewer reviewers list',
        group: 'Reviewer · review',
        pathTemplate: 'asr/reviewer/review/:reviewId/reviewers',
        description: 'Reviewers tab (reviewer role)',
        needsReviewId: true,
        expectedApis: ['users/currentUser', 'reviewers/byReviewId'],
        selected: true
      },
      {
        id: 'ui-reviewer-critiques',
        title: 'Reviewer critique sheets',
        group: 'Reviewer · review',
        pathTemplate: 'asr/reviewer/review/:reviewId/critiqueSheets',
        description: 'Critique sheets list',
        needsReviewId: true,
        expectedApis: ['users/currentUser', 'CritiqueSheets'],
        selected: true
      },
      {
        id: 'ui-reviewer-program-report',
        title: 'Reviewer program report',
        group: 'Reviewer · review',
        pathTemplate: 'asr/reviewer/review/:reviewId/programReport',
        description: 'Program report (reviewer)',
        needsReviewId: true,
        expectedApis: ['users/currentUser', 'ProgramReports'],
        selected: true
      }
    ];
  }
}
